import axios, { AxiosInstance } from 'axios';
import { apiDebug, expectedFormats } from '../utils/debug';
import {
  LoginRequest,
  LoginResponse,
  RefreshRequest,
  User,
  UserCreate,
  UserUpdate,
  UserRoleUpdate,
  Manager,
  Requirement,

  Candidate,
  CandidateCreate,
  CandidateUpdate,
  Submission,
  SubmissionCreate,
  SubmissionUpdate,
  Call,
  CallCreate,

  CallTranscriptUpdate,
  CVAnalysis,
  JDAnalysis,
  FitmentScore,
  InterviewQuestions,
  Query,
  QueryCreate,
  QueryUpdate,
  DashboardKPIs,
  CandidatePipelineAnalytics,
  RecruiterPerformanceAnalytics,
  EmailTemplate,
  EmailSend,
  EmailTracking,
  ComplianceChecklist,
  PipelineStatus,
  AuditLog,

  SearchFilters,
  PaginatedResponse,
  PaginatedUsersResponse,
  ApiResponse,
  JDProcessingInitiated,
  JDProcessingStatus,
  JDProcessingHistory,
  UiPreferences,
} from '../types/api';

export interface RoleScopesPayload {
  mapping: Record<string, string[]>;
}

export async function getRoleScopes(): Promise<RoleScopesPayload> {
  return apiClient.getRoleScopes();
}

export async function updateRoleScopes(payload: RoleScopesPayload): Promise<RoleScopesPayload> {
  return apiClient.updateRoleScopes(payload);
}

class ApiClient {
  public client: AxiosInstance;
  private baseURL: string;
  private isRefreshing = false;
  private refreshPromise: Promise<any> | null = null;

  constructor() {
    // Resolve API base URL using hostname detection (more reliable than build mode)
    const envAny = (import.meta as any).env || {};
    
    // Use hostname to determine environment (same pattern as pdfClient.ts)
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    const isProdByHostname = hostname === 'macha.otomeyt.ai' || hostname === 'recruitedge.otomeyt.ai';
    
    // DEV API: emjbgkwgr8, PROD API: 6oi3xs2kzb
    const DEV_API = 'https://emjbgkwgr8.execute-api.ap-southeast-1.amazonaws.com';
    const PROD_API = 'https://6oi3xs2kzb.execute-api.ap-southeast-1.amazonaws.com';
    
    // Use environment variable override if available, otherwise use hostname detection
    const resolvedBase = isProdByHostname
      ? (envAny.VITE_API_BASE_URL_PROD || PROD_API)
      : (envAny.VITE_API_BASE_URL_DEV || DEV_API);
    
    this.baseURL = resolvedBase;
    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Initialize authorization header if token exists in localStorage
    const existingToken = localStorage.getItem('access_token');
    if (existingToken) {
      this.client.defaults.headers.common.Authorization = `Bearer ${existingToken}`;
      console.log('🔄 API Client: Restored authorization header from localStorage');
    }

    // Request interceptor to add auth token and check expiration
    this.client.interceptors.request.use(
      async (config) => {
        const token = localStorage.getItem('access_token');
        if (token) {
          // Only check token expiration for non-login requests to avoid loops
          const isLoginRequest = config.url?.includes('/auth/login') || config.url?.includes('/auth/refresh');
          if (!isLoginRequest) {
            // Check if token will expire soon and refresh proactively
            const { jwtUtils } = await import('../auth');
            if (jwtUtils.willExpireWithin(token, 2)) { // Refresh if expires within 2 minutes
              console.log('🔄 API Client: Token expires soon, refreshing proactively');
              try {
                const { tokenMonitor } = await import('../services/TokenMonitorService');
                // Check if refresh is already in progress to avoid multiple simultaneous refreshes
                if (!tokenMonitor.isRefreshing) {
                  await tokenMonitor.forceRefresh();
                  // Update token after refresh
                  const newToken = localStorage.getItem('access_token');
                  if (newToken) {
                    config.headers.Authorization = `Bearer ${newToken}`;
                  }
                } else {
                  console.log('🔄 API Client: Refresh already in progress, using current token');
                  config.headers.Authorization = `Bearer ${token}`;
                }
              } catch (error) {
                console.error('❌ API Client: Proactive refresh failed:', error);
                // Continue with original token - let the response interceptor handle it
                config.headers.Authorization = `Bearer ${token}`;
              }
            } else {
              config.headers.Authorization = `Bearer ${token}`;
            }
          } else {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }
        
        // For FormData requests, let the browser set the Content-Type with boundary
        if (config.data instanceof FormData) {
          delete config.headers['Content-Type'];
        }
        
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle token refresh
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        
        // Don't attempt token refresh for login requests - let them fail naturally
        const isLoginRequest = originalRequest.url?.includes('/auth/login');
        
        // Log error for debugging
        if (isLoginRequest) {
          console.log('🔍 API Client: Login request failed with error:', error.response?.data || error.message);
        }
        
        // Only attempt refresh for 401 errors, if we haven't already retried, and it's not a login request
        if (error.response?.status === 401 && !originalRequest._retry && this.shouldAttemptRefresh() && !isLoginRequest) {
          originalRequest._retry = true;
          
          // If we're already refreshing, wait for that to complete
          if (this.isRefreshing && this.refreshPromise) {
            try {
              const response = await this.refreshPromise;
              originalRequest.headers.Authorization = `Bearer ${response.access_token}`;
              return this.client(originalRequest);
            } catch (refreshError) {
              console.error('❌ API Client: Waited for refresh but it failed:', refreshError);
              this.clearAuthToken();
            try {
              const intended = window.location.pathname + window.location.search;
              sessionStorage.setItem('post_login_redirect', intended);
            } catch (e) {
              console.warn('Failed to persist post-login redirect path before redirecting to login', e);
            }
            window.location.href = '/login';
              return Promise.reject(refreshError);
            }
          }
          
          // Start a new refresh
          this.isRefreshing = true;
          this.refreshPromise = this.performTokenRefresh();
          
          try {
            const response = await this.refreshPromise;
            
            // Retry the original request
            originalRequest.headers.Authorization = `Bearer ${response.access_token}`;
            console.log('✅ API Client: Token refresh successful, retrying request');
            return this.client(originalRequest);
          } catch (refreshError) {
            console.error('❌ API Client: Token refresh failed:', refreshError);
            // Refresh failed, clear tokens and redirect to login
            this.clearAuthToken();
            try {
              const intended = window.location.pathname + window.location.search;
              sessionStorage.setItem('post_login_redirect', intended);
            } catch (e) {
              console.warn('Failed to persist post-login redirect path before redirecting to login', e);
            }
            window.location.href = '/login';
            return Promise.reject(refreshError);
          } finally {
            // Reset refresh state
            this.isRefreshing = false;
            this.refreshPromise = null;
          }
        }
        
        return Promise.reject(error);
      }
    );
  }

  // Expose refresh state for consumers (e.g., AuthContext) to avoid clearing tokens mid-refresh
  public isRefreshInProgress(): boolean {
    return this.isRefreshing === true;
  }

  // Reports API
  async getRoleScopes(): Promise<RoleScopesPayload> {
    const response = await this.client.get('/api/v1/admin/roles/scopes');
    return response.data as RoleScopesPayload;
  }

  async updateRoleScopes(payload: RoleScopesPayload): Promise<RoleScopesPayload> {
    const response = await this.client.put('/api/v1/admin/roles/scopes', payload);
    return response.data as RoleScopesPayload;
  }

  // Reports API
  async getSGAReportsByCandidate(candidateId: string): Promise<any[]> {
    const response = await this.client.get(`/api/v1/reports/sga/by-candidate/${candidateId}`);
    return response.data;
  }

  // Screening Sessions API
  async generateScreeningReport(candidateId: string, requirementId: string): Promise<{
    status: 'processing' | 'completed' | 'transcribing';
    message: string;
    merged_session_id?: string;
    included_calls?: number;
    untranscribed_calls?: number;
    total_calls?: number;
  }> {
    const response = await this.client.post('/api/v1/screenings/generate-report', {
      candidate_id: candidateId,
      requirement_id: requirementId
    });
    return response.data;
  }

  // Call Analysis API (Unified Report)
  async generateCallAnalysis(candidateId: string, requirementId: string, forceRegenerate: boolean = false): Promise<{
    status: 'processing' | 'completed' | 'transcribing';
    message: string;
    report_id?: string;
    session_id?: string;
    call_count?: number;
    untranscribed_calls?: number;
    total_calls?: number;
    report?: any;
  }> {
    const response = await this.client.post('/api/v1/screenings/generate-call-analysis', {
      candidate_id: candidateId,
      requirement_id: requirementId,
      force_regenerate: forceRegenerate
    });
    return response.data;
  }

  async getCallAnalysis(candidateId: string, requirementId: string): Promise<{
    exists: boolean;
    status?: string;
    report_id?: string;
    session_id?: string;
    call_count?: number;
    overall_score?: number;
    hire_recommendation?: string;
    created_at?: string;
    message?: string;
    report: any | null;
  }> {
    const response = await this.client.get(`/api/v1/screenings/call-analysis/${candidateId}/${requirementId}`);
    return response.data;
  }

  // Job Tickets API
  async jobsStart(payload: { user_id: string; type: string; anchors?: any; metadata?: any; idempotency_key?: string }): Promise<{ job: any }>{
    const response = await this.client.post('/api/v1/jobs/start', payload);
    return response.data;
  }

  async jobsGet(jobId: string): Promise<{ job: any }>{
    const response = await this.client.get(`/api/v1/jobs/${jobId}`);
    return response.data;
  }

  async jobsList(userId: string, status?: string, limit: number = 20): Promise<{ jobs: any[] }>{
    const response = await this.client.get('/api/v1/jobs', { params: { user_id: userId, status, limit } });
    return response.data;
  }

  async jobsUpdate(jobId: string, body: { status?: string; progress?: number; anchors?: any; resume_url?: string; result_url?: string; message?: string }): Promise<{ job: any }>{
    const response = await this.client.post(`/api/v1/jobs/${jobId}/update`, body);
    return response.data;
  }

  async getSGAReport(candidateId: string, reportId: string): Promise<any> {
    const response = await this.client.get(`/api/v1/reports/sga/${candidateId}/${reportId}`);
    return response.data;
  }

  async downloadSGAReportPdf(candidateId: string, reportId: string): Promise<Blob> {
    const response = await this.client.get(`/api/v1/reports/sga/${candidateId}/${reportId}/pdf`, { responseType: 'blob' });
    return response.data as Blob;
  }

  async getCallAuditReportsByRecruiter(recruiterId: string): Promise<any[]> {
    const response = await this.client.get(`/api/v1/reports/audit/by-recruiter/${recruiterId}`);
    return response.data;
  }

  async getCallAuditReport(recruiterId: string, reportId: string): Promise<any> {
    const response = await this.client.get(`/api/v1/reports/audit/${recruiterId}/${reportId}`);
    return response.data;
  }

  async downloadCallAuditPdf(recruiterId: string, reportId: string): Promise<Blob> {
    const response = await this.client.get(`/api/v1/reports/audit/${recruiterId}/${reportId}/pdf`, { responseType: 'blob' });
    return response.data as Blob;
  }

  async sendCombinedReportEmail(params: {
    candidate_id: string;
    recruiter_id: string;
    to_email: string;
    subject?: string;
    html_body?: string;
    text_body?: string;
  }): Promise<{ status: string }>{
    const response = await this.client.post(`/api/v1/reports/email/send`, params);
    return response.data;
  }

  async suggestCombinedReportEmail(candidateId: string, recruiterId: string, audience: 'client' | 'submission' = 'client'): Promise<{ subject: string; html_body: string; text_body: string }>{
    const response = await this.client.get(`/api/v1/reports/email/suggest`, {
      params: { candidate_id: candidateId, recruiter_id: recruiterId, audience }
    });
    return response.data;
  }

  // Call Audit Params
  async uploadCallAuditParams(orgId: string, teamLeadId: string, file: File): Promise<any> {
    const form = new FormData();
    form.append('file', file);
    const response = await this.client.post(`/api/v1/call-audit-params/upload/${orgId}/${teamLeadId}`, form, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  async downloadCallAuditParamsTemplateCsv(): Promise<Blob> {
    const response = await this.client.get(`/api/v1/call-audit-params/template.csv`, { responseType: 'blob' });
    return response.data as Blob;
  }

  async getCallAuditParams(orgId: string, teamLeadId: string): Promise<any> {
    const response = await this.client.get(`/api/v1/call-audit-params/${orgId}/${teamLeadId}`);
    return response.data;
  }

  async setCallAuditParams(orgId: string, teamLeadId: string, body: any): Promise<any> {
    const response = await this.client.post(`/api/v1/call-audit-params/set/${orgId}/${teamLeadId}`, body);
    return response.data;
  }

  async updateCallAuditParams(orgId: string, teamLeadId: string, body: any): Promise<any> {
    const response = await this.client.put(`/api/v1/call-audit-params/${orgId}/${teamLeadId}`, body);
    return response.data;
  }

  async deleteCallAuditParams(orgId: string, teamLeadId: string): Promise<any> {
    const response = await this.client.delete(`/api/v1/call-audit-params/${orgId}/${teamLeadId}`);
    return response.data;
  }

  // Health Check Endpoints
  async getHealth(): Promise<ApiResponse<any>> {
    const response = await this.client.get('/api/v1/health/');
    return response.data;
  }

  async getHealthReady(): Promise<ApiResponse<any>> {
    const response = await this.client.get('/api/v1/health/ready');
    return response.data;
  }

  async getHealthDetailed(): Promise<ApiResponse<any>> {
    const response = await this.client.get('/api/v1/health/detailed');
    return response.data;
  }

  async getHealthMetrics(): Promise<ApiResponse<any>> {
    const response = await this.client.get('/api/v1/health/metrics');
    return response.data;
  }

  // Generic HTTP Methods
  async get<T = any>(url: string): Promise<T> {
    const response = await this.client.get<T>(url);
    return response.data;
  }

  async post<T = any>(url: string, data?: any): Promise<T> {
    const response = await this.client.post<T>(url, data);
    return response.data;
  }

  // Authentication Endpoints
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const endpoint = '/api/v1/auth/login';
    
    // Log request
    apiDebug.request(endpoint, 'POST', credentials);
    
    try {
      const response = await this.client.post<LoginResponse>(endpoint, credentials);
      
      // Log successful response
      apiDebug.response(endpoint, response.status, response.data, expectedFormats.login);
      
      // Log authentication state
      apiDebug.auth('LOGIN_SUCCESS', { 
        hasAccessToken: !!response.data.access_token,
        hasRefreshToken: !!response.data.refresh_token,
        tokenType: response.data.token_type,
        expiresIn: response.data.expires_in
      });
      
      return response.data;
    } catch (error) {
      // Log error
      apiDebug.error(endpoint, error);
      throw error;
    }
  }

  async refreshToken(refreshData: RefreshRequest): Promise<LoginResponse> {
    const response = await this.client.post<LoginResponse>('/api/v1/auth/refresh', refreshData);
    return response.data;
  }


  private async performTokenRefresh(): Promise<LoginResponse> {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }
    
    console.log('🔄 API Client: Performing token refresh...');
    const response = await this.refreshToken({ refresh_token: refreshToken });
    
    // Update tokens
    localStorage.setItem('access_token', response.access_token);
    localStorage.setItem('refresh_token', response.refresh_token);
    this.client.defaults.headers.common.Authorization = `Bearer ${response.access_token}`;
    
    console.log('✅ API Client: Token refresh completed successfully');
    return response;
  }

  async logout(): Promise<void> {
    await this.client.post('/api/v1/auth/logout');
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  }

  // Removed duplicate getCurrentUser method - using the one below with proper return type

  // Admin Endpoints
  async getSystemConfig(): Promise<ApiResponse<any>> {
    const response = await this.client.get('/api/v1/admin/config');
    return response.data;
  }

  async updateSystemConfig(config: Record<string, any>): Promise<ApiResponse<any>> {
    const response = await this.client.post('/api/v1/admin/config', config);
    return response.data;
  }

  async getUsers(filters?: SearchFilters): Promise<User[]> {
    const response = await this.client.get('/api/v1/admin/users', { params: filters });
    return response.data.users || [];
  }

  async getUsersPaginated(filters?: SearchFilters & { page?: number; page_size?: number; search?: string; role?: string; org_id?: string; is_active?: boolean }): Promise<PaginatedUsersResponse<User>> {
    const response = await this.client.get('/api/v1/admin/users', { params: filters });
    return response.data;
  }

  // Requirements API methods
  async getRequirements(filters?: {
    page?: number;
    page_size?: number;
    status_filter?: string;
    skills?: string;
    location?: string;
    seniority?: string;
    search?: string;
    client_filter?: string;
    sort_by?: string;
    sort_order?: string;
  }): Promise<any> {
    const response = await this.client.get('/api/v1/recruiter/requirements', { params: filters });
    return response.data;
  }

  // Optimized Requirements List API - Returns minimal data for table display
  async getRequirementsList(filters?: {
    page?: number;
    page_size?: number;
    search?: string;
    status?: string;
    client_id?: string;
    category?: string;
    seniority?: string;
    experience_min?: number;
    experience_max?: number;
    sort_by?: string;
    sort_order?: string;
  }): Promise<any> {
    const response = await this.client.get('/api/v1/recruiter/requirements/list', { params: filters });
    return response.data;
  }

  async getRequirement(id: string): Promise<any> {
    const response = await this.client.get(`/api/v1/recruiter/requirements/${id}`);
    return response.data;
  }

  async createRequirement(data: any): Promise<any> {
    const response = await this.client.post('/api/v1/recruiter/requirements', data);
    return response.data;
  }

  async updateRequirement(id: string, data: any): Promise<any> {
    const response = await this.client.put(`/api/v1/recruiter/requirements/${id}`, data);
    return response.data;
  }

  async deleteRequirement(id: string): Promise<void> {
    await this.client.delete(`/api/v1/recruiter/requirements/${id}`);
  }

  async pinRequirement(id: string): Promise<void> {
    await this.client.post(`/api/v1/recruiter/requirements/${id}/pin`);
  }

  async unpinRequirement(id: string): Promise<void> {
    await this.client.delete(`/api/v1/recruiter/requirements/${id}/pin`);
  }

  // Client API methods
  async getClients(filters?: {
    search?: string;
    status?: string;
    page?: number;
    size?: number;
  }): Promise<any> {
    const response = await this.client.get('/api/v1/clients', { params: filters });
    return response.data;
  }

  async getClientSPOCs(clientId: string): Promise<Array<{ id: string; name: string; email?: string; phone?: string }>> {
    const response = await this.client.get(`/api/v1/clients/${clientId}/spocs`);
    return response.data;
  }

  async getClient(id: string): Promise<any> {
    const response = await this.client.get(`/api/v1/clients/${id}`);
    return response.data;
  }

  async createClient(data: any): Promise<any> {
    const response = await this.client.post('/api/v1/clients', data);
    return response.data;
  }

  async updateClient(id: string, data: any): Promise<any> {
    const response = await this.client.put(`/api/v1/clients/${id}`, data);
    return response.data;
  }

  async deleteClient(id: string): Promise<void> {
    await this.client.delete(`/api/v1/clients/${id}`);
  }

  async restoreClient(id: string): Promise<void> {
    await this.client.post(`/api/v1/clients/${id}/restore`);
  }

  async getActiveClientsList(): Promise<any[]> {
    const response = await this.client.get('/api/v1/clients/active/list');
    return response.data;
  }

  // Clients bulk upload (admin-only)
  async downloadClientsTemplateCsv(): Promise<Blob> {
    const response = await this.client.get('/api/v1/clients/bulk/template.csv', { responseType: 'blob' });
    return response.data as Blob;
  }

  async bulkUploadClients(file: File): Promise<{
    processed: number;
    created: number;
    skipped_duplicates: number;
    errors: number;
    results: Array<{ row: number; client_name: string; status: string; message?: string }>;
  }> {
    const form = new FormData();
    form.append('file', file);
    const response = await this.client.post('/api/v1/clients/bulk-upload', form, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  // Note: parseJobDescription method removed - use parseJobDescriptionAsync instead

  async parseJobDescriptionAsync(data: { file?: File; text?: string }): Promise<JDProcessingInitiated> {
    console.log('🚀 API Client: parseJobDescriptionAsync called with:', {
      hasFile: !!data.file,
      fileName: data.file?.name,
      fileSize: data.file?.size,
      fileType: data.file?.type,
      hasText: !!data.text,
      textLength: data.text?.length
    });
    
    const formData = new FormData();
    
    if (data.file) {
      console.log('📁 API Client: Appending file to FormData:', data.file.name);
      formData.append('file', data.file);
    }
    
    if (data.text) {
      console.log('📝 API Client: Appending text to FormData, length:', data.text.length);
      formData.append('raw_jd', data.text);
    }
    
    console.log('🚀 API Client: Sending async request to /api/v1/recruiter/jd-parse');
    
    const response = await this.client.post<JDProcessingInitiated>('/api/v1/recruiter/jd-parse', formData);
    console.log('✅ API Client: Async processing initiated:', response.data);
    return response.data;
  }

  async getJDProcessingStatus(processingId: string): Promise<JDProcessingStatus> {
    console.log('🔍 API Client: Getting JD processing status for:', processingId);
    
    const response = await this.client.get<JDProcessingStatus>(`/api/v1/recruiter/jd-parse/status/${processingId}`);
    console.log('✅ API Client: Status retrieved:', response.data);
    return response.data;
  }

  async getJDProcessingHistory(limit: number = 10): Promise<JDProcessingHistory> {
    console.log('🔍 API Client: Getting JD processing history, limit:', limit);
    
    const response = await this.client.get<JDProcessingHistory>(`/api/v1/recruiter/jd-parse/history?limit=${limit}`);
    console.log('✅ API Client: History retrieved:', response.data);
    return response.data;
  }

  async createUser(userData: UserCreate): Promise<User> {
    const response = await this.client.post<User>('/api/v1/admin/users', userData);
    return response.data;
  }

  async updateUser(userId: string, userData: UserUpdate): Promise<User> {
    const response = await this.client.put<User>(`/api/v1/admin/users/${userId}`, userData);
    return response.data;
  }

  async deleteUser(userId: string): Promise<void> {
    await this.client.delete(`/api/v1/admin/users/${userId}`);
  }

  async resetUserPassword(userId: string): Promise<{ message: string; email_sent: boolean }> {
    const response = await this.client.post(`/api/v1/admin/users/${userId}/reset-password`);
    return response.data;
  }

  async forgotPassword(email: string): Promise<{ message: string; timestamp: string }> {
    const response = await this.client.post('/api/v1/auth/forgot-password', { email });
    return response.data;
  }

  async resetPassword(token: string, newPassword: string): Promise<{ message: string }> {
    const response = await this.client.post('/api/v1/auth/reset-password', { token, new_password: newPassword });
    return response.data;
  }

  async resendUserEmail(userId: string): Promise<{ message: string; email: string }> {
    const response = await this.client.post<{ message: string; email: string }>(`/api/v1/admin/users/${userId}/resend-email`);
    return response.data;
  }

  async submitSupportTicket(data: { subject: string; message: string; priority: string; category: string }): Promise<{ message: string }> {
    const response = await this.client.post<{ message: string }>('/api/v1/support/ticket', data);
    return response.data;
  }

  // PDF Generation - Now handled by separate PDF service
  // See pdfClient.ts for PDF generation functionality

  async updateUserRole(userId: string, roleData: UserRoleUpdate): Promise<User> {
    const response = await this.client.put<User>(`/api/v1/admin/users/${userId}/role`, roleData);
    return response.data;
  }

  // Hierarchy-related API methods
  async getHierarchyInfo(): Promise<{
    user_role: string;
    user_id: string;
    hierarchy_config: {
      can_see: string;
      can_manage: string;
      scope: string;
      description: string;
    };
    manageable_roles: string[];
    can_manage_users: boolean;
    scope: string;
    description: string;
  }> {
    const response = await this.client.get('/api/v1/admin/users/hierarchy-info');
    return response.data;
  }

  async getAssignedUsers(filters?: {
    page?: number;
    page_size?: number;
    search?: string;
    role?: string;
  }): Promise<PaginatedUsersResponse<User>> {
    const response = await this.client.get('/api/v1/admin/users/assigned', { params: filters });
    return response.data;
  }

  async getPipelineStatuses(): Promise<PipelineStatus[]> {
    const response = await this.client.get('/api/v1/admin/statuses');
    return response.data;
  }

  async createPipelineStatus(statusData: Omit<PipelineStatus, 'id' | 'created_at' | 'updated_at'>): Promise<PipelineStatus> {
    const response = await this.client.post<PipelineStatus>('/api/v1/admin/statuses', statusData);
    return response.data;
  }

  async updatePipelineStatus(statusId: string, statusData: Partial<PipelineStatus>): Promise<PipelineStatus> {
    const response = await this.client.put<PipelineStatus>(`/api/v1/admin/statuses/${statusId}`, statusData);
    return response.data;
  }

  async deletePipelineStatus(statusId: string): Promise<void> {
    await this.client.delete(`/api/v1/admin/statuses/${statusId}`);
  }

  async getComplianceChecklists(): Promise<ComplianceChecklist[]> {
    const response = await this.client.get('/api/v1/admin/compliance');
    return response.data;
  }

  async createComplianceChecklist(checklistData: Omit<ComplianceChecklist, 'id' | 'created_at' | 'updated_at'>): Promise<ComplianceChecklist> {
    const response = await this.client.post<ComplianceChecklist>('/api/v1/admin/compliance', checklistData);
    return response.data;
  }

  async updateComplianceChecklist(checklistId: string, checklistData: Partial<ComplianceChecklist>): Promise<ComplianceChecklist> {
    const response = await this.client.put<ComplianceChecklist>(`/api/v1/admin/compliance/${checklistId}`, checklistData);
    return response.data;
  }

  async deleteComplianceChecklist(checklistId: string): Promise<void> {
    await this.client.delete(`/api/v1/admin/compliance/${checklistId}`);
  }

  async getAuditLogs(filters?: SearchFilters): Promise<PaginatedResponse<AuditLog>> {
    const response = await this.client.get('/api/v1/admin/audit', { params: filters });
    return response.data;
  }

  async getSystemSettings(): Promise<ApiResponse<any>> {
    const response = await this.client.get('/api/v1/admin/settings');
    return response.data;
  }

  async updateSystemSettings(settings: Record<string, any>): Promise<ApiResponse<any>> {
    const response = await this.client.post('/api/v1/admin/settings', settings);
    return response.data;
  }

  // Admin: Users bulk sample and upload
  async downloadUsersSample(): Promise<Blob> {
    const response = await this.client.get(`/api/v1/admin/users/bulk/sample`, { responseType: 'blob' });
    return response.data as Blob;
  }

  async bulkCreateUsers(file: File, sendEmails = true): Promise<{ created_count: number; error_count: number; created: any[]; errors: any[] }> {
    const form = new FormData();
    form.append('file', file);
    const response = await this.client.post(`/api/v1/admin/users/bulk`, form, {
      params: { send_emails: sendEmails },
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  // Recruiter Endpoints
  async getRecruiterDashboard(): Promise<DashboardKPIs> {
    const response = await this.client.get('/api/v1/recruiter/dashboard');
    return response.data;
  }

  async getActiveRequirements(filters?: SearchFilters): Promise<PaginatedResponse<Requirement>> {
    const response = await this.client.get('/api/v1/recruiter/requirements', { params: filters });
    return response.data;
  }

  async getRequirementDetails(requirementId: string): Promise<Requirement> {
    const response = await this.client.get(`/api/v1/recruiter/requirements/${requirementId}`);
    return response.data;
  }

  async regenerateBooleanQueries(requirementId: string): Promise<{
    message: string;
    requirement_id: string;
    status: string;
    estimated_time: string;
    instructions: string;
  }> {
    const response = await this.client.post(`/api/v1/recruiter/requirements/${requirementId}/regenerate-boolean-queries`);
    return response.data;
  }

  async createCandidate(candidateData: CandidateCreate, requirementId?: string): Promise<Candidate> {
    const formData = new FormData();
    Object.entries(candidateData).forEach(([key, value]) => {
      if (value !== undefined) {
        if (key === 'cv_file' && value instanceof File) {
          formData.append(key, value);
        } else {
          formData.append(key, String(value));
        }
      }
    });

    // Add requirement_id as query parameter if provided
    const params = requirementId ? { requirement_id: requirementId } : {};

    const response = await this.client.post<Candidate>('/api/v1/recruiter/candidates', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      params,
    });
    return response.data;
  }

  async bulkUploadCandidates(candidates: CandidateCreate[]): Promise<Candidate[]> {
    const response = await this.client.post<Candidate[]>('/api/v1/recruiter/candidates/bulk', { candidates });
    return response.data;
  }

  // Bulk resume upload (presigned S3 + confirm)
  async bulkResumeInitiate(requirementId: string, files: Array<{ filename: string; size?: number; contentType?: string; clientHash?: string }>, userId?: string): Promise<{
    job_id: string;
    uploads: Array<{ filename: string; s3Key: string; url: string; fields: Record<string, string>; itemId: string }>;
    total_files: number;
  }> {
    const payload = {
      requirement_id: requirementId,
      user_id: userId,
      files: files.map(f => ({ filename: f.filename, size: f.size, content_type: f.contentType, clientHash: f.clientHash }))
    };
    const response = await this.client.post('/api/v1/bulk-resume/initiate', payload);
    return response.data;
  }

  async bulkResumeConfirm(jobId: string, requirementId: string, items: Array<{ s3Key: string; filename: string; itemId: string; clientHash?: string }>): Promise<{ queued: number }>{
    const body = {
      job_id: jobId,
      requirement_id: requirementId,
      items: items.map(i => ({ s3Key: i.s3Key, filename: i.filename, itemId: i.itemId, clientHash: i.clientHash }))
    };
    const response = await this.client.post('/api/v1/bulk-resume/confirm', body);
    return response.data;
  }

  async getCandidates(filters?: SearchFilters): Promise<PaginatedResponse<Candidate>> {
    const response = await this.client.get('/api/v1/recruiter/candidates', { params: filters });
    return response.data;
  }

  async updateCandidate(candidateId: string, candidateData: CandidateUpdate): Promise<Candidate> {
    const response = await this.client.put<Candidate>(`/api/v1/recruiter/candidates/${candidateId}`, candidateData);
    return response.data;
  }

  async deleteCandidate(candidateId: string): Promise<void> {
    await this.client.delete(`/api/v1/recruiter/candidates/${candidateId}`);
  }

  async updateCandidateStatus(
    candidateId: string,
    data: { status: string; requirement_id: string; notes?: string }
  ): Promise<{
    success: boolean;
    candidate_id: string;
    requirement_id: string;
    previous_status: string | null;
    new_status: string;
    status_category: string;
    updated_at: string;
    updated_by: string;
    notes?: string;
  }> {
    const response = await this.client.put(
      `/api/v1/recruiter/candidates/${candidateId}/status`,
      data
    );
    return response.data;
  }

  async getCandidateStatusHistory(
    candidateId: string,
    requirementId?: string
  ): Promise<{
    candidate_id: string;
    requirement_id: string | null;
    current_status: string | null;
    history: Array<{
      status: string;
      previous_status: string | null;
      status_category: string;
      updated_at: string;
      updated_by: string;
      notes?: string;
    }>;
  }> {
    const params = requirementId ? { requirement_id: requirementId } : {};
    const response = await this.client.get(
      `/api/v1/recruiter/candidates/${candidateId}/status-history`,
      { params }
    );
    return response.data;
  }

  async getCandidate(candidateId: string): Promise<Candidate | null> {
    // Check if candidateId looks like an email (contains @)
    if (candidateId.includes('@')) {
      // Use the candidates list endpoint with search to find the candidate by email
      const response = await this.client.get<PaginatedResponse<Candidate>>('/api/v1/recruiter/candidates', {
        params: { search: candidateId, page_size: 1 }
      });
      
      if (response.data.items && response.data.items.length > 0) {
        // Find the exact match (case-insensitive)
        const candidate = response.data.items.find(c => 
          c.email?.toLowerCase() === candidateId.toLowerCase() || 
          c.id?.toLowerCase() === candidateId.toLowerCase()
        );
        if (candidate) {
          return candidate;
        }
      }
    } else {
      // For non-email IDs (like generated hash IDs), try the original candidate detail endpoint
      try {
        const response = await this.client.get<Candidate>(`/api/v1/recruiter/candidates/${candidateId}`);
        return response.data;
      } catch (error: any) {
        // If the detail endpoint fails, fall back to searching all candidates
        console.log(`Direct lookup failed for ${candidateId}, trying search fallback...`);
      }
    }
    
    // For CV upload scenarios, the candidate might not be processed yet
    // Return null instead of throwing an error to allow graceful handling
    console.log(`Candidate with ID ${candidateId} not found - may still be processing`);
    return null;
  }

  async getCandidateResumeUrl(candidateId: string, opts?: { disposition?: 'inline' | 'attachment'; requirementId?: string }): Promise<{ url: string; expires_in: number }> {
    const params: any = {};
    if (opts?.disposition) params.disposition = opts.disposition;
    if (opts?.requirementId) params.requirement_id = opts.requirementId;
    const response = await this.client.get(`/api/v1/recruiter/candidates/${candidateId}/resume/download`, { params });
    return response.data;
  }

  async generateAnalysisPDF(candidateId: string, opts?: { requirementId?: string; reportType?: 'client' | 'internal' }): Promise<{ message: string; message_id: string; status: string }> {
    const params: any = {};
    if (opts?.requirementId) params.requirement_id = opts.requirementId;
    if (opts?.reportType) params.report_type = opts.reportType;
    const response = await this.client.get(`/api/v1/recruiter/candidates/${candidateId}/analysis/pdf`, { params });
    return response.data;
  }

  async getAnalysisPDFStatus(candidateId: string, messageId: string): Promise<{ status: string; message: string; pdf_url?: string }> {
    const response = await this.client.get(`/api/v1/recruiter/candidates/${candidateId}/analysis/pdf/status`, { 
      params: { message_id: messageId } 
    });
    return response.data;
  }

  async uploadCV(file: File, candidateId?: string, requirementId?: string): Promise<{ candidate_id: string; cv_path: string; status: string; message: string }> {
    const formData = new FormData();
    formData.append('cv_file', file);
    if (candidateId) {
      formData.append('candidate_id', candidateId);
    }
    if (requirementId) {
      formData.append('requirement_id', requirementId);
    }
    
    const response = await this.client.post('/api/v1/recruiter/candidates/upload-resume', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async getCVProcessingStatus(candidateId: string, requirementId?: string): Promise<{ status: string; message: string; parsed_fields?: any }> {
    const url = requirementId 
      ? `/api/v1/recruiter/candidates/${candidateId}/resume-status?requirement_id=${requirementId}`
      : `/api/v1/recruiter/candidates/${candidateId}/resume-status`;
    const response = await this.client.get(url);
    return response.data;
  }

  async uploadResume(file: File): Promise<{ candidate_id: string; cv_path: string; status: string; message: string; sqs_message_id: string }> {
    const formData = new FormData();
    formData.append('cv_file', file);

    const response = await this.client.post('/api/v1/recruiter/candidates/upload-resume', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async getResumeProcessingStatus(candidateId: string): Promise<{ 
    candidate_id: string; 
    status: string; 
    message: string; 
    parsed_fields?: any; 
    progress?: number;
    error?: string;
    processing_completed_at?: string;
  }> {
    const response = await this.client.get(`/api/v1/recruiter/candidates/${candidateId}/resume-status`);
    return response.data;
  }

  async triggerStage2Processing(candidateId: string): Promise<{ message: string; candidate_id: string; status: string }> {
    const response = await this.client.post(`/api/v1/candidates/${candidateId}/trigger-stage2`);
    return response.data;
  }

  async getCandidateAnalysisResults(candidateId: string, requirementId: string): Promise<any> {
    try {
      // Use the new JD vs CV analysis endpoint
      const response = await this.client.get(`/api/v1/recruiter/candidates/${candidateId}/analysis/${requirementId}/results`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null; // Analysis not found yet
      }
      throw error;
    }
  }

  async triggerAnalysis(candidateId: string, requirementId: string): Promise<any> {
    const response = await this.client.post(`/api/v1/recruiter/candidates/${candidateId}/analyze/${requirementId}`);
    return response.data;
  }

  // JD vs CV Analysis Methods
  async triggerJDCVAnalysis(candidateId: string, requirementId: string): Promise<{
    analysis_id: string;
    candidate_id: string;
    requirement_id: string;
    status: string;
    message: string;
    sqs_message_id?: string;
  }> {
    const response = await this.client.post(`/api/v1/recruiter/candidates/${candidateId}/analyze/${requirementId}`);
    return response.data;
  }

  async adminReprocessCandidateJDCV(
    candidateId: string,
    requirementId: string,
    options?: { clearIdentity?: boolean }
  ): Promise<{
    message: string;
    candidate_id: string;
    requirement_id: string;
    cv_path: string;
    content_type?: string;
  }> {
    const response = await this.client.post(
      `/api/v1/admin/candidates/${candidateId}/requirements/${requirementId}/reprocess-jd-cv`,
      null,
      {
        params: {
          clear_identity: options?.clearIdentity ?? false,
        },
      }
    );
    return response.data;
  }

  async getJDCVAnalysisStatus(candidateId: string, requirementId: string): Promise<{
    status: string;
    message: string;
    candidate_id: string;
    requirement_id: string;
    updated_at?: string;
  }> {
    const response = await this.client.get(`/api/v1/recruiter/candidates/${candidateId}/analysis/${requirementId}/status`);
    return response.data;
  }

  async getJDCVAnalysisResults(candidateId: string, requirementId: string): Promise<{
    analysis_id: string;
    candidate_id: string;
    requirement_id: string;
    results: {
      overall_fitment_score: number | string;
      skill_analysis: {
        match_percentage: number | string;
        matched_skills: string[];
        missing_skills: string[];
      };
      experience_analysis: {
        candidate_experience: number | string;
        required_experience: number | string;
        experience_gap: number | string;
      };
      education_analysis: {
        candidate_education: string;
        required_education: string;
        education_match: boolean;
      };
      hygiene_questions: string[];
      interview_questions: string[];
      analysis_timestamp?: string;
      analysis_method?: string;
    };
    created_at: string;
    updated_at: string;
  }> {
    const response = await this.client.get(`/api/v1/recruiter/candidates/${candidateId}/analysis/${requirementId}/results`);
    return response.data;
  }

  async submitCandidateToRequirement(candidateId: string, requirementId: string): Promise<Submission> {
    const formData = new FormData();
    formData.append('requirement_id', requirementId);
    
    const response = await this.client.post(`/api/v1/candidates/${candidateId}/submit-to-requirement`, formData);
    return response.data;
  }

  async submitCandidate(submissionData: SubmissionCreate): Promise<Submission> {
    const response = await this.client.post<Submission>('/api/v1/recruiter/submissions', submissionData);
    return response.data;
  }

  async getSubmissions(filters?: SearchFilters): Promise<PaginatedResponse<Submission>> {
    const response = await this.client.get('/api/v1/recruiter/submissions', { params: filters });
    return response.data;
  }

  // Requirements API methods
  async getRequirementCandidates(requirementId: string, page: number = 1, pageSize: number = 20): Promise<{
    candidates: Candidate[];
    total: number;
    page: number;
    page_size: number;
    total_pages: number;
    has_next: boolean;
    has_previous: boolean;
    requirement_id: string;
  }> {
    const response = await this.client.get(`/api/v1/recruiter/requirements/${requirementId}/candidates`, {
      params: { page, page_size: pageSize }
    });
    return response.data;
  }

  async getRecruitersForRequirement(requirementId: string): Promise<Array<{ id: string; name: string; email: string }>> {
    const response = await this.client.get(`/api/v1/requirements/${requirementId}/recruiters`);
    return response.data;
  }

  async getCandidateSubmissions(candidateId: string, page: number = 1, pageSize: number = 20): Promise<{
    submissions: any[];
    total: number;
    page: number;
    page_size: number;
    total_pages: number;
    has_next: boolean;
    has_previous: boolean;
    candidate_id: string;
  }> {
    const response = await this.client.get(`/api/v1/recruiter/candidates/${candidateId}/submissions`, {
      params: { page, page_size: pageSize }
    });
    return response.data;
  }

  async getRequirementsCandidateCounts(requirementIds: string[]): Promise<{
    candidate_counts: Record<string, number>;
    total_requirements: number;
  }> {
    const response = await this.client.get('/api/v1/recruiter/candidate-counts', {
      params: { requirement_ids: requirementIds.join(',') }
    });
    return response.data;
  }

  async updateSubmissionStatus(submissionId: string, statusData: SubmissionUpdate): Promise<Submission> {
    const response = await this.client.put<Submission>(`/api/v1/recruiter/submissions/${submissionId}/status`, statusData);
    return response.data;
  }

  // Analysis Endpoints
  async startJdCvAnalysis(candidateId: string, requirementId: string): Promise<{ analysis_id: string; status: string; message: string; estimated_completion: string }> {
    const response = await this.client.post(`/api/v1/candidates/${candidateId}/analyze/${requirementId}`);
    return response.data;
  }

  // UI Preferences Endpoints
  async getUiPreferences(): Promise<UiPreferences> {
    const response = await this.client.get(`/api/v1/auth/me/preferences`);
    return response.data;
  }

  async updateUiPreferences(prefs: UiPreferences): Promise<UiPreferences> {
    const response = await this.client.put(`/api/v1/auth/me/preferences`, prefs);
    return response.data;
  }

  async getAnalysisStatus(analysisId: string): Promise<{
    analysis_id: string;
    status: string;
    progress: number;
    message: string;
    created_at: string;
    updated_at: string;
    fitment_score?: number;
    strengths?: string[];
    gaps?: string[];
    overall_assessment?: string;
    interview_questions?: string[];
    risks?: string[];
  }> {
    const response = await this.client.get(`/api/v1/candidates/analysis/${analysisId}/status`);
    return response.data;
  }

  async getAnalysisResults(analysisId: string): Promise<{
    analysis_id: string;
    candidate_id: string;
    requirement_id: string;
    fitment_score: number;
    overall_assessment: string;
    strengths: string[];
    gaps: string[];
    interview_questions: string[];
    risks: string[];
    recommendations: string[];
    created_at: string;
    completed_at: string;
  }> {
    const response = await this.client.get(`/api/v1/candidates/analysis/${analysisId}/results`);
    return response.data;
  }

  async getCandidateAnalyses(candidateId: string): Promise<{
    analyses: Array<{
      analysis_id: string;
      requirement_id: string;
      status: string;
      fitment_score?: number;
      created_at: string;
      completed_at?: string;
    }>;
  }> {
    const response = await this.client.get(`/api/v1/candidates/${candidateId}/analyses`);
    return response.data;
  }

  async getLatestAnalysisForCandidate(candidateId: string, requirementId: string): Promise<any> {
    const response = await this.client.get(`/api/v1/candidates/${candidateId}/analyses/latest`, {
      params: { requirement_id: requirementId }
    });
    return response.data;
  }

  async getSubmissionAnalysis(submissionId: string): Promise<{ status: string; progress: number; analysis?: any }> {
    const response = await this.client.get(`/api/v1/recruiter/submissions/${submissionId}/analysis`);
    return response.data;
  }

  // Comprehensive Analysis API methods

  async retryComprehensiveAnalysis(candidateId: string): Promise<{ message: string; status: string }> {
    const response = await this.client.post(`/api/v1/recruiter/candidates/${candidateId}/retry-comprehensive-analysis`);
    return response.data;
  }

  async retryJdCvAnalysis(candidateId: string, requirementId: string): Promise<{ message: string; status: string }> {
    const response = await this.client.post(`/api/v1/recruiter/candidates/${candidateId}/retry-jd-cv-analysis`, {
      requirement_id: requirementId
    });
    return response.data;
  }

  async submitCandidateForm(candidateData: any): Promise<{ candidate_id: string; status: string; message: string }> {
    const response = await this.client.post('/api/v1/recruiter/candidates/submit-form', candidateData);
    return response.data;
  }

  async createCall(callData: CallCreate): Promise<Call> {
    const response = await this.client.post<Call>('/api/v1/recruiter/calls', callData);
    return response.data;
  }

  async getCalls(filters?: SearchFilters): Promise<PaginatedResponse<Call>> {
    const response = await this.client.get('/api/v1/recruiter/calls', { params: filters });
    return response.data;
  }

  async updateCallTranscript(callId: string, transcriptData: CallTranscriptUpdate): Promise<Call> {
    const response = await this.client.put<Call>(`/api/v1/recruiter/calls/${callId}/transcript`, transcriptData);
    return response.data;
  }

  async getCandidatePipelineAnalytics(filters?: SearchFilters): Promise<CandidatePipelineAnalytics> {
    const response = await this.client.get('/api/v1/recruiter/analytics/candidate-pipeline', { params: filters });
    return response.data;
  }

  async getRecruiterPerformanceAnalytics(filters?: SearchFilters): Promise<RecruiterPerformanceAnalytics[]> {
    const response = await this.client.get('/api/v1/recruiter/analytics/performance', { params: filters });
    return response.data;
  }

  // AI Analysis Endpoints
  async analyzeCV(cvFile: File, requirementId?: string): Promise<CVAnalysis> {
    const formData = new FormData();
    formData.append('cv_file', cvFile);
    if (requirementId) {
      formData.append('requirement_id', requirementId);
    }

    const response = await this.client.post<CVAnalysis>('/api/v1/ai/analyze-cv', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async analyzeJD(jdFile: File): Promise<JDAnalysis> {
    const formData = new FormData();
    formData.append('jd_file', jdFile);

    const response = await this.client.post<JDAnalysis>('/api/v1/ai/analyze-jd', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async calculateFitmentScore(candidateId: string, requirementId: string): Promise<FitmentScore> {
    const response = await this.client.post<FitmentScore>('/api/v1/ai/fitment-score', {
      candidate_id: candidateId,
      requirement_id: requirementId,
    });
    return response.data;
  }

  async generateInterviewQuestions(candidateId: string, requirementId: string): Promise<InterviewQuestions> {
    const response = await this.client.post<InterviewQuestions>('/api/v1/ai/generate-questions', {
      candidate_id: candidateId,
      requirement_id: requirementId,
    });
    return response.data;
  }


  async exportAnalysisAsPDF(analysisId: string): Promise<Blob> {
    const response = await this.client.get(`/api/v1/ai/analysis/${analysisId}/export`, {
      responseType: 'blob',
    });
    return response.data;
  }

  // Query System Endpoints
  async generateQuery(queryData: QueryCreate): Promise<Query> {
    const response = await this.client.post<Query>('/api/v1/queries/generate', queryData);
    return response.data;
  }

  async getQueries(filters?: SearchFilters): Promise<PaginatedResponse<Query>> {
    const response = await this.client.get('/queries', { params: filters });
    return response.data;
  }

  async getQueryDetails(queryId: string): Promise<Query> {
    const response = await this.client.get(`/api/v1/queries/${queryId}`);
    return response.data;
  }

  async updateQuery(queryId: string, queryData: QueryUpdate): Promise<Query> {
    const response = await this.client.put<Query>(`/api/v1/queries/${queryId}`, queryData);
    return response.data;
  }

  async deleteQuery(queryId: string): Promise<void> {
    await this.client.delete(`/api/v1/queries/${queryId}`);
  }

  async approveQuery(queryId: string): Promise<Query> {
    const response = await this.client.post<Query>(`/api/v1/queries/${queryId}/approve`);
    return response.data;
  }

  async rejectQuery(queryId: string, reason?: string): Promise<Query> {
    const response = await this.client.post<Query>(`/api/v1/queries/${queryId}/reject`, { reason });
    return response.data;
  }

  async crossMatchCandidates(candidateIds: string[], requirementIds: string[]): Promise<ApiResponse<any>> {
    const response = await this.client.post('/api/v1/queries/cross-match', {
      candidate_ids: candidateIds,
      requirement_ids: requirementIds,
    });
    return response.data;
  }

  // Calling System Endpoints (removed duplicates - using new implementations below)

  async getCallReport(callId: string): Promise<Blob> {
    const response = await this.client.get(`/api/v1/calls/${callId}/report`, {
      responseType: 'blob',
    });
    return response.data;
  }

  // Email System Endpoints
  async sendEmail(emailData: EmailSend): Promise<ApiResponse<any>> {
    const response = await this.client.post('/api/v1/email/send', emailData);
    return response.data;
  }

  async createEmailDraft(emailData: EmailSend): Promise<ApiResponse<any>> {
    const response = await this.client.post('/api/v1/email/draft', emailData);
    return response.data;
  }

  async getEmailTemplates(): Promise<EmailTemplate[]> {
    const response = await this.client.get('/api/v1/email/templates');
    return response.data;
  }

  async getEmailTracking(trackingId: string): Promise<EmailTracking> {
    const response = await this.client.get(`/api/v1/email/tracking/${trackingId}`);
    return response.data;
  }


  // Requirement Value Management
  async getRequirementValues() {
    const response = await this.client.get('/api/v1/admin/requirement-values');
    return response.data;
  }

  async getRequirementValuesByCategory(category: string) {
    const response = await this.client.get(`/api/v1/admin/requirement-values/${category}`);
    return response.data;
  }

  async createRequirementValue(category: string, data: {
    value: string;
    display_name: string;
    description?: string;
    sort_order?: number;
    metadata?: Record<string, any>;
  }) {
    const response = await this.client.post(`/api/v1/admin/requirement-values/${category}`, data);
    return response.data;
  }

  async updateRequirementValue(category: string, valueId: string, data: {
    value?: string;
    display_name?: string;
    description?: string;
    sort_order?: number;
    metadata?: Record<string, any>;
  }) {
    const response = await this.client.put(`/api/v1/admin/requirement-values/${category}/${valueId}`, data);
    return response.data;
  }

  async deleteRequirementValue(category: string, valueId: string) {
    const response = await this.client.delete(`/api/v1/admin/requirement-values/${category}/${valueId}`);
    return response.data;
  }

  async bulkUpdateRequirementValues(category: string, data: {
    value_orders: Array<{ value_id: string; sort_order: number }>;
  }) {
    const response = await this.client.post(`/api/v1/admin/requirement-values/${category}/bulk-update`, data);
    return response.data;
  }

  async importDefaultRequirementValues(category: string, data: {
    overwrite_existing?: boolean;
  } = {}) {
    const response = await this.client.post(`/api/v1/admin/requirement-values/${category}/import-defaults`, data);
    return response.data;
  }

  async activateRequirementValue(category: string, valueId: string) {
    const response = await this.client.post(`/api/v1/admin/requirement-values/${category}/activate/${valueId}`);
    return response.data;
  }

  async deactivateRequirementValue(category: string, valueId: string) {
    const response = await this.client.post(`/api/v1/admin/requirement-values/${category}/deactivate/${valueId}`);
    return response.data;
  }

  async searchRequirementValues(category: string, params: {
    query?: string;
    page?: number;
    size?: number;
  } = {}) {
    const searchParams = new URLSearchParams();
    if (params.query) searchParams.append('query', params.query);
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.size) searchParams.append('size', params.size.toString());

    const response = await this.client.get(`/api/v1/admin/requirement-values/${category}/search?${searchParams}`);
    return response.data;
  }

  // Call methods
  async initiateCall(data: {
    candidate_phone: string;
    candidate_id: string;
    requirement_id: string;
  }): Promise<{
    success: boolean;
    call_sid?: string;
    message: string;
    session_id?: string;
    script_data?: any;
  }> {
    const response = await this.client.post('/api/v1/recruiter/initiate-call', data);
    return response.data;
  }

  async getCallStatus(callSid: string): Promise<{
    call_sid: string;
    status: string;
    event_type?: string;
    date_updated?: string;
    conversation_duration?: number;
    recording_url?: string;
  }> {
    const response = await this.client.get(`/api/v1/calls/${callSid}/status`);
    return response.data;
  }

  async getCallRecording(callSid: string): Promise<{
    call_sid: string;
    recording_url: string;
    conversation_duration?: number;
  }> {
    const response = await this.client.get(`/api/v1/calls/${callSid}/recording`);
    return response.data;
  }

  async analyzeCall(data: { call_sid: string }): Promise<{
    call_sid: string;
    analysis_id: string;
    status: string;
    created_at: string;
    updated_at: string;
  }> {
    const response = await this.client.post('/api/v1/calls/analyze', data);
    return response.data;
  }

  async getCandidateCalls(candidateId: string, page = 1, pageSize = 20): Promise<{
    items: any[];
    total: number;
    page: number;
    page_size: number;
    has_next: boolean;
    has_previous: boolean;
  }> {
    const response = await this.client.get(`/api/v1/recruiter/calls/${candidateId}`, {
      params: { page, page_size: pageSize }
    });
    return response.data;
  }

  // User Profile Methods
  async getCurrentUser(): Promise<User> {
    const response = await this.client.get('/api/v1/auth/me');
    return response.data;
  }

  async updateUserPhone(data: { phone: string }): Promise<User> {
    const response = await this.client.put('/api/v1/auth/me/phone', data);
    return response.data;
  }

  async updateUserProfile(data: { 
    first_name?: string; 
    last_name?: string; 
    phone?: string; 
    exotel_virtual_number?: string; 
    exotel_agent_number?: string; 
  }): Promise<User> {
    const response = await this.client.put('/api/v1/auth/me/profile', data);
    return response.data;
  }

  async changePassword(data: { current_password: string; new_password: string }): Promise<{ message: string; timestamp?: string }> {
    const response = await this.client.post('/api/v1/auth/change-password', data);
    return response.data;
  }

  async setPassword(data: { new_password: string }): Promise<{ message: string; timestamp?: string }> {
    const response = await this.client.post('/api/v1/auth/set-password', data);
    return response.data;
  }

  // Profile Completion Endpoints
  async completeUserProfile(data: {
    email: string;
    newPassword: string;
    phoneNumber: string;
    managerId?: string;
  }): Promise<{ message: string; user: User }> {
    const response = await this.client.post('/api/v1/auth/complete-profile', data);
    return response.data;
  }

  async searchManagers(query: string): Promise<Manager[]> {
    const response = await this.client.get(`/api/v1/auth/managers/search?q=${encodeURIComponent(query)}`);
    return response.data;
  }

  // Cold Call Methods
  async getCallSessions(candidateId: string, requirementId: string): Promise<{
    success: boolean;
    sessions?: any[];
    message?: string;
  }> {
    console.log('🔍 getCallSessions: Base URL:', this.baseURL);
    console.log('🔍 getCallSessions: Full URL:', `${this.baseURL}/api/v1/recruiter/call-sessions/${candidateId}/${requirementId}`);
    const response = await this.client.get(`/api/v1/recruiter/call-sessions/${candidateId}/${requirementId}`);
    return response.data;
  }

  async getColdCallScript(data: {
    candidate_id: string;
    requirement_id: string;
  }): Promise<{
    success: boolean;
    script_data?: any;
    message?: string;
  }> {
    console.log('🔍 getColdCallScript: Base URL:', this.baseURL);
    console.log('🔍 getColdCallScript: Full URL:', `${this.baseURL}/api/v1/recruiter/get-cold-call-script`);
    console.log('🔍 getColdCallScript: Data:', data);
    const response = await this.client.post('/api/v1/recruiter/get-cold-call-script', data);
    return response.data;
  }

  async startColdCall(data: {
    candidate_phone: string;
    candidate_id: string;
    requirement_id: string;
  }): Promise<{
    success: boolean;
    call_sid?: string;
    session_id?: string;
    message: string;
    script_data?: any;
  }> {
    console.log('🔍 startColdCall: Base URL:', this.baseURL);
    console.log('🔍 startColdCall: Full URL:', `${this.baseURL}/api/v1/recruiter/start-cold-call`);
    console.log('🔍 startColdCall: Data:', data);
    const response = await this.client.post('/api/v1/recruiter/start-cold-call', data);
    return response.data;
  }

  // VOIP Methods
  async getVoipWebSDKToken(): Promise<{
    access_token: string;
    app_user_id: string;
    expires_in: number;
    enabled: boolean;
  }> {
    const response = await this.client.get('/api/v1/recruiter/voip-websdk-token');
    return response.data;
  }

  async logVoipCall(data: {
    candidate_id: string;
    requirement_id: string;
    session_id: string;
    voip_session_id?: string;
    candidate_phone: string;
  }): Promise<{
    success: boolean;
    call_sid: string;
    message: string;
  }> {
    const response = await this.client.post('/api/v1/recruiter/voip-call-log', data);
    return response.data;
  }

  async endVoipCall(data: {
    call_sid: string;
    status: 'completed' | 'failed' | 'no-answer' | 'busy';
    duration: number;
    candidate_id?: string;
    requirement_id?: string;
    recording_url?: string;
  }): Promise<{
    success: boolean;
    call_sid: string;
    status: string;
    duration: number;
    message: string;
  }> {
    const response = await this.client.post('/api/v1/recruiter/voip-call-end', data);
    return response.data;
  }

  async getVoipSettings(): Promise<{
    voip_enabled: boolean;
    user_voip_enabled: boolean;
    voip_username: string | null;
    sip_uri: string | null;
    voip_domain: string;
    message: string;
  }> {
    const response = await this.client.get('/api/v1/recruiter/voip-settings');
    return response.data;
  }

  async updateVoipSettings(data: {
    voip_enabled: boolean;
    voip_username?: string;
    voip_password?: string;
  }): Promise<{
    success: boolean;
    voip_enabled: boolean;
    voip_username: string | null;
    sip_uri: string | null;
    message: string;
  }> {
    const response = await this.client.post('/api/v1/recruiter/voip-settings', data);
    return response.data;
  }

  async startColdCallV3(data: {
    candidate_phone: string;
    candidate_id: string;
    requirement_id: string;
  }): Promise<{
    success: boolean;
    call_sid?: string;
    session_id?: string;
    api_version: string;
    connection_type: string;
    is_voip: boolean;
    from_uri?: string;
    message: string;
    script_data?: any;
  }> {
    const response = await this.client.post('/api/v1/recruiter/initiate-call-v3', data);
    return response.data;
  }

  async updateCallPhase(data: {
    session_id: string;
    action: 'next' | 'previous';
    notes?: string;
  }): Promise<{
    success: boolean;
    session_id: string;
    current_phase: number;
    script_data: any;
  }> {
    const response = await this.client.post('/api/v1/recruiter/update-call-phase', data);
    return response.data;
  }

  async endColdCall(data: {
    session_id: string;
    recording_url?: string;
  }): Promise<{
    success: boolean;
    session_id: string;
    status: string;
    recording_url?: string;
    can_evaluate: boolean;
  }> {
    const response = await this.client.post('/api/v1/recruiter/end-cold-call', data);
    return response.data;
  }

  async getCallSession(sessionId: string): Promise<{
    success: boolean;
    session: any;
    script_data: any;
  }> {
    const response = await this.client.get(`/api/v1/recruiter/call-session/${sessionId}`);
    return response.data;
  }

  // Report Generation API Methods
  async generateReport(data: {
    candidate_id: string;
    requirement_id: string;
    recruiter_id?: string;
    call_ids?: string[];
    analysis_types?: string[];
  }): Promise<{
    success: boolean;
    report_id: string;
    status: string;
    message: string;
    calls_processed: number;
    estimated_completion: string | null;
  }> {
    const response = await this.client.post('/api/v1/recruiter/calls/generate-report', data);
    return response.data;
  }

  async getReportStatus(reportId: string): Promise<{
    report_id: string;
    status: 'processing' | 'completed' | 'failed';
    progress: {
      recordings_processed: number;
      recordings_total: number;
      transcription_status: string;
      analysis_status: string;
    };
    call_count: number;
    created_at: string;
    completed_at: string | null;
    error_message: string | null;
  }> {
    const response = await this.client.get(`/api/v1/recruiter/reports/${reportId}/status`);
    return response.data;
  }

  async getReport(reportId: string): Promise<{
    report_id: string;
    status: string;
    candidate_id: string;
    requirement_id: string;
    recruiter_id: string;
    call_ids: string[];
    call_count: number;
    total_duration_seconds: number;
    results: {
      combined_transcript?: {
        full_text: string;
        segments: any[];
        word_count: number;
        call_count: number;
        total_duration_seconds: number;
      };
      sga_report?: any;
      call_audit_report?: any;
      s3_audio_keys?: string[];
    };
    created_at: string;
    completed_at: string;
  }> {
    const response = await this.client.get(`/api/v1/recruiter/reports/${reportId}`);
    return response.data;
  }

  async getCandidateReports(
    candidateId: string,
    requirementId?: string
  ): Promise<{
    success: boolean;
    candidate_id: string;
    requirement_id: string | null;
    reports: Array<{
      report_id: string;
      status: string;
      call_count: number;
      requirement_id: string;
      created_at: string;
      completed_at: string | null;
    }>;
  }> {
    const params: any = {};
    if (requirementId) {
      params.requirement_id = requirementId;
    }
    const response = await this.client.get(`/api/v1/recruiter/candidates/${candidateId}/reports`, { params });
    return response.data;
  }

  async getCallCount(
    candidateId: string,
    requirementId: string,
    recruiterId?: string
  ): Promise<{
    success: boolean;
    candidate_id: string;
    requirement_id: string;
    call_count: number;
    call_ids: string[];
  }> {
    const params: any = {
      candidate_id: candidateId,
      requirement_id: requirementId
    };
    if (recruiterId) {
      params.recruiter_id = recruiterId;
    }
    const response = await this.client.get('/api/v1/recruiter/calls/count', { params });
    return response.data;
  }

  async sendJDEmail(data: {
    candidate_id: string;
    requirement_id: string;
  }): Promise<{
    success: boolean;
    message_id?: string;
    status: string;
    error?: string;
  }> {
    console.log('🔍 sendJDEmail: Base URL:', this.baseURL);
    console.log('🔍 sendJDEmail: Full URL:', `${this.baseURL}/api/v1/recruiter/send-jd-email`);
    console.log('🔍 sendJDEmail: Data:', data);
    const response = await this.client.post('/api/v1/recruiter/send-jd-email', data);
    return response.data;
  }

  async getEmailAnalytics(recruiterId?: string, days = 30): Promise<{
    success: boolean;
    analytics: {
      total_sent: number;
      delivered: number;
      opened: number;
      clicked: number;
      bounced: number;
      complained: number;
      delivery_rate: number;
      open_rate: number;
      click_rate: number;
      bounce_rate: number;
      complaint_rate: number;
    };
    period_days: number;
  }> {
    const params: any = { days };
    if (recruiterId) params.recruiter_id = recruiterId;
    
    const response = await this.client.get('/api/v1/recruiter/email-analytics', { params });
    return response.data;
  }

  // Workforce Insights API
  async getWorkforceSummary(days = 7): Promise<any> {
    const response = await this.client.get(`/api/v1/workforce/summary`, { params: { days } });
    return response.data;
  }

  async getWorkforceKPIs(days = 7): Promise<any> {
    const response = await this.client.get(`/api/v1/workforce/kpis`, { params: { days } });
    return response.data;
  }

  async getWorkforceActions(days = 7): Promise<{ items: any[]; total: number }>{
    const response = await this.client.get(`/api/v1/workforce/actions`, { params: { days } });
    return response.data;
  }

  async getWorkforceClients(days = 7): Promise<{ items: any[]; total: number; lookback_days: number }>{
    const response = await this.client.get(`/api/v1/workforce/clients`, { params: { days } });
    return response.data;
  }

  async getWorkforceAlerts(days = 7): Promise<{ items: any[]; total: number; lookback_days: number }>{
    const response = await this.client.get(`/api/v1/workforce/alerts`, { params: { days } });
    return response.data;
  }

  async getWorkforceDigest(days = 1): Promise<any>{
    const response = await this.client.get(`/api/v1/workforce/digest`, { params: { days } });
    return response.data;
  }

  async getWorkforceInsights(limit = 25, next_key?: string): Promise<{ items: any[]; next_key?: string | null; count: number }>{
    const params: any = { limit };
    if (next_key) params.next_key = next_key;
    const response = await this.client.get(`/api/v1/workforce/insights`, { params });
    return response.data;
  }

  async getWorkforceThread(threadId: string, limit = 50, next_key?: string): Promise<{ items: any[]; next_key?: string | null; count: number }>{
    const params: any = { limit };
    if (next_key) params.next_key = next_key;
    const response = await this.client.get(`/api/v1/workforce/threads/${threadId}`, { params });
    return response.data;
  }

  // Utility Methods
  setAuthToken(token: string): void {
    localStorage.setItem('access_token', token);
    this.client.defaults.headers.common.Authorization = `Bearer ${token}`;
  }

  clearAuthToken(): void {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    delete this.client.defaults.headers.common.Authorization;
  }

  isAuthenticated(): boolean {
    return !!localStorage.getItem('access_token');
  }

  shouldAttemptRefresh(): boolean {
    // Only attempt refresh if we have a refresh token and aren't already refreshing
    return !!localStorage.getItem('refresh_token') && !this.isRefreshing;
  }

  getAuthToken(): string | null {
    return localStorage.getItem('access_token');
  }

  async bulkUploadRequirementsCsv(file: File): Promise<{ processed: number; created: number; errors: number; results: any[] }>{
    const form = new FormData();
    form.append('file', file);
    const response = await this.client.post('/api/v1/recruiter/requirements/bulk-upload', form, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }
}

// Create and export a singleton instance
export const apiClient = new ApiClient();

// Export the class for testing purposes
export default ApiClient;
